import { createRequestHeaders } from '@zg-rentals/particles-js-requester';
import DispatchConstants from '@common/constants/DispatchConstants';
import AppSelectors from '@rental-manager/selectors/AppSelectors';
import type { ErrorInterface } from '@common/types';
import { logError } from '@zg-rentals/log-error';
import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger('requesterLog');

const requestWrapper =
  (request, methodName, endpoint, config: Record<string, any> = {}) =>
  async (dispatch, getState) => {
    const state = getState();

    let endpointUrl = endpoint?.replace(/^\/+/, '');

    endpointUrl = __BROWSER__ ? `${window.location.protocol}//${window.location.host}/${endpointUrl}` : endpointUrl;

    const cookieObj = getState().user.originalServerSideRequestCookies;
    // used for server side requests
    // fixes bug where userAgent is always node/axios
    const userAgent = state.app.userAgent;

    const requestConfig = {
      ...config,
      headers: createRequestHeaders({
        brand: AppSelectors.makeBrand(state),
        ip: state.app.ip,
        userAgent,
        headers: {
          'X-Brand': AppSelectors.makeBrand(state),
        },
        url: endpoint,
        method: methodName,
        cookies: {
          ...cookieObj,
          ...(state.app.hostApp
            ? {
                hostApp: state.app.hostApp,
              }
            : {}),
        },
      }),
    };

    try {
      const response = await request[methodName](
        endpoint,
        config.data ? config.data : requestConfig,
        config.data ? requestConfig : undefined,
      );

      logger?.info({
        methodName,
        endpointUrl,
        msg: 'parsing requester response',
        status: response.status,
        statusText: response.statusText,
        data: response.data,
      });

      if (response.data.error) {
        const error = new Error(response.data.error);
        logError({ error, context: 'requestWrapper' });
        return Promise.reject(error);
      }

      // TODO-AJD: do we need ssr?
      // update server side request cookies based on api response
      // used by renderer.js to return the correct cookeis in header of o.g. request
      const apiResponseServerSideRequestCookies = response.headers['set-cookie'];
      if (apiResponseServerSideRequestCookies) {
        dispatch({
          type: DispatchConstants.UPDATE_USER_INFO,
          payload: {
            apiResponseServerSideRequestCookies,
          },
        });
      }

      // dispatch requester success action for inspection with redux devtools
      dispatch({
        type: '@@REQUESTER/SUCCESS',
        payload: response.data,
      });

      return response.data;
    } catch (err) {
      const error = err as ErrorInterface;
      logError({ error, context: { methodName, endpoint, config: error.config } });

      // dispatch requester fail action for inspection with redux devtools
      dispatch({
        type: '@@REQUESTER/FAIL',
        payload: err,
      });

      return {
        success: false,
      };
    }
  };

export default requestWrapper;
